import { Card, Typography } from 'antd'
import { t } from 'i18next'
import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useFieldArray, useFormContext } from 'react-hook-form'
import styled from 'styled-components'
import { isNil } from 'lodash'

import { Button } from 'src/common'
import { EducationSection } from './EducationSection'
import { TEducation } from '@interfaces'
import { TEducations } from 'src/pages/auth/sign-up/fill-in-teacher-information/types'
import { PATH_UPDATE_APPLICATION } from '@configs'
import { TEACHER_INFO_SPECIFIC_INFO_VALIDATION } from 'src/pages/auth/sign-up/configs'

export const Educations = ({ isViewMode, canEdit = false }: TEducations) => {
  const { pathname } = useLocation()
  const isUpdatingApplication = [PATH_UPDATE_APPLICATION].includes(pathname)

  const { control, getValues, setValue, watch } = useFormContext()

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'specificInformation.education',
    rules: {
      minLength: TEACHER_INFO_SPECIFIC_INFO_VALIDATION.MIN_LENGTH,
      maxLength: TEACHER_INFO_SPECIFIC_INFO_VALIDATION.MAX_LENGTH,
    },
  })

  const removableField = fields?.length > 1 && !isViewMode
  const [isAddableField, setAddableField] = React.useState<boolean>(false)

  const watchEducationValue = watch('specificInformation.education')
  const isEditting = watchEducationValue?.some((it: any) => !!it?.editable)

  const verifyAddableField = () => {
    return (
      watchEducationValue?.length <
      TEACHER_INFO_SPECIFIC_INFO_VALIDATION.MAX_LENGTH
    )
  }

  const handleRemoveField = (id?: number) => {
    remove(id)

    const isAddableValue =
      watchEducationValue?.length >=
      TEACHER_INFO_SPECIFIC_INFO_VALIDATION.MAX_LENGTH
        ? true
        : verifyAddableField()
    setAddableField(isAddableValue)
  }

  const handleSaveField = (id?: number) => {
    if (isNil(id)) {
      return
    }
    update(id, getValues(`specificInformation.education[${id}]`))
    setAddableField(verifyAddableField())
  }

  const handleAddField = () => {
    const defaultFieldValue: TEducation = {
      institution: '',
      educationLevel: '',
      qualification: '',
      description: '',
      startDate: '',
      endDate: '',
      editable: true,
      isPresent: false,
      degree: {
        file: {
          name: '',
          fileId: 0,
        },
      },
    }
    append(defaultFieldValue)
    setAddableField(false)
  }

  const handleEditField = (id?: number) => {
    if (isNil(id)) {
      return
    }
    setValue(`specificInformation.education[${id}].editable`, true)
    update(id, getValues(`specificInformation.education[${id}]`))
    setAddableField(false)
  }

  useEffect(() => {
    if (isUpdatingApplication || canEdit) {
      setAddableField(true)
    }
  }, [canEdit, isUpdatingApplication])

  return (
    <WorkExperiencesStyled className="flex items-center justify-center w-full">
      <Typography.Title level={4} className="text-left">
        Education Information
      </Typography.Title>
      {fields?.map((field: any, index) => {
        return (
          <EducationSection
            key={field.id}
            data={field}
            index={index}
            onRemove={removableField ? handleRemoveField : undefined}
            onSave={handleSaveField}
            onEdit={isViewMode ? undefined : handleEditField}
          />
        )
      })}

      {isAddableField && !isEditting ? (
        <div className="flex items-center max-[800px] justify-center my-5">
          <Button
            htmlType="submit"
            type="primary"
            size="small"
            className="min-[600px] max-[600px]:!w-full"
            onClick={handleAddField}
          >
            {t('common:addEducation')}
          </Button>
        </div>
      ) : null}
    </WorkExperiencesStyled>
  )
}

const WorkExperiencesStyled = styled(Card)`
  .ant-card-body {
  }

  .reset-form {
    > div:last-child {
      justify-content: center;
    }
  }
`
